import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy, 
  limit 
} from 'firebase/firestore'
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth'
import { db, auth } from './firebase'

// Export Firebase functions for use in other files
export { collection, addDoc }

// Authentication Services

// Register customer (client registration)
export const registerUser = async (email, password, userData) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user
    
    // Update user profile
    await updateProfile(user, {
      displayName: userData.fullName
    })
    
    // Save customer data to Firestore users collection with UID as document ID
    const userRef = doc(db, 'users', user.uid)
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      fullName: userData.fullName,
      phone: userData.phone,
      address: userData.address || '',
      city: userData.city || '',
      country: userData.country || '',
      bankAccount: userData.bankAccount || '',
      bankName: userData.bankName || '',
      role: 'customer', // All registered users are customers
      disabled: false,
      vip: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid,
      updatedBy: user.uid
    })
    
    return user
  } catch (error) {
    throw error
  }
}

// Get current user role (admin, manager, customer)
export const getCurrentUserRole = async () => {
  const user = getCurrentUser()
  if (!user) return null
  
  try {
    // Check if admin
    const adminRef = doc(db, 'admins', user.uid)
    const adminSnap = await getDoc(adminRef)
    if (adminSnap.exists()) {
      return { type: 'admin', data: adminSnap.data() }
    }
    
    // Check if manager or customer
    const userRef = doc(db, 'users', user.uid)
    const userSnap = await getDoc(userRef)
    if (userSnap.exists()) {
      const userData = userSnap.data()
      return { type: userData.role || 'customer', data: userData }
    }
    
    return null
  } catch (error) {
    console.error('Error getting user role:', error)
    return null
  }
}

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    return userCredential.user
  } catch (error) {
    throw error
  }
}

export const logoutUser = async () => {
  try {
    await signOut(auth)
  } catch (error) {
    throw error
  }
}

export const getCurrentUser = () => {
  return auth.currentUser
}

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback)
}

// Contact Services
export const getContactInfo = async () => {
  const querySnapshot = await getDocs(collection(db, 'contact'))
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

export const addContactInfo = async (contactData) => {
  try {
    const docRef = await addDoc(collection(db, 'contact'), contactData)
    return docRef.id
  } catch (e) {
    console.error("Error adding contact info: ", e)
    throw e
  }
}

export const updateContactInfo = async (id, contactData) => {
  try {
    const docRef = doc(db, 'contact', id)
    await updateDoc(docRef, contactData)
  } catch (e) {
    console.error("Error updating contact info: ", e)
    throw e
  }
}

// About Services
export const getAboutInfo = async () => {
  const querySnapshot = await getDocs(collection(db, 'about'))
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

export const addAboutInfo = async (aboutData) => {
  try {
    const docRef = await addDoc(collection(db, 'about'), aboutData)
    return docRef.id
  } catch (e) {
    console.error("Error adding about info: ", e)
    throw e
  }
}

export const updateAboutInfo = async (id, aboutData) => {
  try {
    const docRef = doc(db, 'about', id)
    await updateDoc(docRef, aboutData)
  } catch (e) {
    console.error("Error updating about info: ", e)
    throw e
  }
}

// Tours Collection
export const toursCollection = collection(db, 'tours')

// Get all tours
export const getAllTours = async () => {
  try {
    const querySnapshot = await getDocs(toursCollection)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting tours:', error)
    throw error
  }
}

// Get tour by ID
export const getTourById = async (tourId) => {
  try {
    const tourDoc = doc(db, 'tours', tourId)
    const tourSnap = await getDoc(tourDoc)
    
    if (tourSnap.exists()) {
      return {
        id: tourSnap.id,
        ...tourSnap.data()
      }
    } else {
      throw new Error('Tour not found')
    }
  } catch (error) {
    console.error('Error getting tour:', error)
    throw error
  }
}

// Get featured tours
export const getFeaturedTours = async () => {
  try {
    const q = query(toursCollection, where('isFeatured', '==', true), limit(6))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting featured tours:', error)
    throw error
  }
}

// Get tours by category
export const getToursByCategory = async (category) => {
  try {
    const q = query(toursCollection, where('category', '==', category))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting tours by category:', error)
    throw error
  }
}

// Search tours
export const searchTours = async (searchTerm) => {
  try {
    const q = query(
      toursCollection,
      where('title', '>=', searchTerm),
      where('title', '<=', searchTerm + '\uf8ff')
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error searching tours:', error)
    throw error
  }
}

// Add new tour
export const addTour = async (tourData, userId) => {
  try {
    const docRef = await addDoc(toursCollection, {
      ...tourData,
      disabled: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
      updatedBy: userId
    })
    return docRef.id
  } catch (error) {
    console.error('Error adding tour:', error)
    throw error
  }
}

// Update tour
export const updateTour = async (tourId, updateData, userId) => {
  try {
    const tourRef = doc(db, 'tours', tourId)
    await updateDoc(tourRef, {
      ...updateData,
      updatedAt: new Date(),
      updatedBy: userId
    })
  } catch (error) {
    console.error('Error updating tour:', error)
    throw error
  }
}

// Disable/Enable tour (soft delete for managers)
export const disableTour = async (tourId, userId, disable = true) => {
  try {
    const tourRef = doc(db, 'TOURS', tourId)
    await updateDoc(tourRef, {
      disabled: disable,
      updatedAt: new Date(),
      updatedBy: userId
    })
  } catch (error) {
    console.error('Error disabling tour:', error)
    throw error
  }
}

// Disable/Enable Post
export const disablePost = async (postId, userId, disable = true) => {
  try {
    const postRef = doc(db, 'POSTS', postId)
    await updateDoc(postRef, {
      disabled: disable,
      updatedAt: new Date(),
      updatedBy: userId
    })
  } catch (error) {
    console.error('Error disabling post:', error)
    throw error
  }
}

// Delete tour
export const deleteTour = async (tourId) => {
  try {
    await deleteDoc(doc(db, 'tours', tourId))
  } catch (error) {
    console.error('Error deleting tour:', error)
    throw error
  }
}

// Destinations Collection
export const destinationsCollection = collection(db, 'destinations')

// Get all destinations
export const getAllDestinations = async () => {
  try {
    const querySnapshot = await getDocs(destinationsCollection)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting destinations:', error)
    throw error
  }
}

// Bookings Collection
export const bookingsCollection = collection(db, 'bookings')

// Add booking
export const addBooking = async (bookingData) => {
  try {
    const docRef = await addDoc(bookingsCollection, {
      ...bookingData,
      createdAt: new Date(),
      updatedAt: new Date()
    })
    return docRef.id
  } catch (error) {
    console.error('Error adding booking:', error)
    throw error
  }
}

// Get user bookings
export const getUserBookings = async (userId) => {
  try {
    const q = query(bookingsCollection, where('userId', '==', userId))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting user bookings:', error)
    throw error
  }
}

// Reviews Collection
export const reviewsCollection = collection(db, 'reviews')

// Get tour reviews
export const getTourReviews = async (tourId) => {
  try {
    const q = query(reviewsCollection, where('tourId', '==', tourId), orderBy('createdAt', 'desc'))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting tour reviews:', error)
    throw error
  }
}

// Add review
export const addReview = async (reviewData) => {
  try {
    const docRef = await addDoc(reviewsCollection, {
      ...reviewData,
      createdAt: new Date()
    })
    return docRef.id
  } catch (error) {
    console.error('Error adding review:', error)
    throw error
  }
}

// Comments Collection
export const commentsCollection = collection(db, 'COMMENTS')

// Get comments for a specific content (tour or blog post)
export const getComments = async (contentType, contentId) => {
  try {
    // Simplified query without status filter to avoid index requirement
    const q = query(
      commentsCollection,
      where('contentType', '==', contentType),
      where('contentId', '==', contentId),
      orderBy('createdAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    // Filter approved comments in code instead
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(comment => comment.status === 'approved' || !comment.status) // Show if approved or no status field
  } catch (error) {
    console.error('Error getting comments:', error)
    // Return empty array instead of throwing to avoid breaking UI
    console.warn('Returning empty comments array due to error')
    return []
  }
}

// Add comment
export const addComment = async (commentData) => {
  try {
    const docRef = await addDoc(commentsCollection, {
      ...commentData,
      status: 'approved', // Auto-approve comments (can be changed to 'pending' for moderation)
      createdAt: new Date(),
      updatedAt: new Date()
    })
    return docRef.id
  } catch (error) {
    console.error('Error adding comment:', error)
    throw error
  }
}

// Update comment
export const updateComment = async (commentId, updateData) => {
  try {
    const commentRef = doc(db, 'COMMENTS', commentId)
    await updateDoc(commentRef, {
      ...updateData,
      updatedAt: new Date()
    })
  } catch (error) {
    console.error('Error updating comment:', error)
    throw error
  }
}

// Delete comment
export const deleteComment = async (commentId) => {
  try {
    await deleteDoc(doc(db, 'COMMENTS', commentId))
  } catch (error) {
    console.error('Error deleting comment:', error)
    throw error
  }
}

// Get user's comments
export const getUserComments = async (userId) => {
  try {
    const q = query(
      commentsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting user comments:', error)
    throw error
  }
}